/**
 * QuickAddUsersModal - add one or more people right where they're needed.
 *
 * Atomic Design role: Organism. Mounted once by QuickAddUsersProvider and
 * opened from every people picker on a work order — the "+ Quick Add User"
 * link (and dropdown action) under Assign To / Additional Assignees, the
 * customer contact pickers, the hours editors, and the "Hours Worked" prompt
 * at sign-off / hand-off. The person often isn't in the system yet; bouncing
 * to Admin → Users would lose the half-filled form, so they are added here and
 * handed straight back to the picker that asked (as an assignee, an hours
 * row, or the selected contact). Text typed into the search that opened the
 * modal pre-fills the first person's name.
 *
 * Each person is a QuickAddPersonFieldset card:
 *   - Type: CSR (default) / JSR / Full User — limited to what the creator's
 *     own role may assign (GET /users/available-roles).
 *   - CSR / JSR: first + last name and primary business; email, phone and
 *     password optional.
 *   - Full User: also email, phone and password (a Technician sign-in account,
 *     welcomed by email).
 *   - Primary business defaults to the work order's business and is a
 *     search-to-filter picker.
 * "+ Add another person" appends a card, so a whole crew can be added at once.
 *
 * Saving creates the people one at a time (POST /users). Anyone who saves is
 * reported back through `onCreated` immediately — even if a later person
 * fails — and only the failed cards stay on screen with the server's reason.
 * Once everyone is in, a summary lists who was added and any temporary
 * passwords to share.
 *
 * Rendered through a portal to <body>: it opens from inside other forms and
 * overlays, and must neither nest a <form> inside theirs nor let its clicks,
 * submits or Escape key reach them (React events bubble through portals, and
 * the workflow overlay closes on Escape).
 *
 * @example
 * // Normally opened through useQuickAddUsers().openQuickAdd(...), not rendered directly.
 * <QuickAddUsersModal isOpen={open} onClose={close} defaultCompanyId={workOrder.companyId} onCreated={addRows} />
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import Modal from '../Modal/Modal';
import { Button } from '../../atoms/Button';
import { Alert } from '../../atoms/Alert';
import { Spinner } from '../../atoms/Spinner';
import { LinkButton } from '../../atoms/LinkButton';
import { QuickAddPersonFieldset } from '../../molecules/QuickAddPersonFieldset';
import { TempPasswordDisplay } from '../../molecules/TempPasswordDisplay';
import { dedupeAndSort } from '../../../utils/dedupeAndSort';
import { apiErrorMessage } from '../../../utils/apiErrorMessage';
import {
	availableQuickAddTypes,
	DEFAULT_QUICK_ADD_TYPE,
	emptyQuickAddDraft,
	quickAddPayload,
	quickAddType,
	splitName,
	validateQuickAddDraft,
} from '../../../utils/quickAddUser';
import type { QuickAddDraft, QuickAddDraftErrors, QuickAddedPersonRef, QuickAddPersonType, QuickAddTypeConfig } from '../../../utils/quickAddUser';
import { userService, type CompanyOption } from '../../../services/userService';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** A person the modal created — id, name and any contact details. */
export type QuickAddedPerson = QuickAddedPersonRef;

/** One line of the success summary. */
interface CreatedSummary extends QuickAddedPerson {
	typeLabel: string;
	tempPassword: string | null;
}

export interface QuickAddUsersModalProps {
	/** Whether the modal is visible. */
	isOpen: boolean;
	/** Called when the modal should close (X, Escape, backdrop, Done, Cancel). */
	onClose: () => void;
	/** The work order's business — pre-selected as each person's primary business. */
	defaultCompanyId?: string | number | null;
	/** Fired with everyone saved by a submit, as soon as they are saved. */
	onCreated: (people: QuickAddedPerson[]) => void;
	/** Text typed into the search that opened us — pre-fills the first name fields. */
	initialName?: string;
	/** One person only (e.g. a contact): hides "+ Add another person". */
	single?: boolean;
}

/** Id linking the <form> to the submit button pinned in the modal footer. */
const FORM_ID = 'quick-add-users-form';

/* ============================================
 * COMPONENT
 * ============================================ */

export const QuickAddUsersModal: React.FC<QuickAddUsersModalProps> = ({ isOpen, onClose, defaultCompanyId, onCreated, initialName = '', single = false }) => {
	const defaultCompany = defaultCompanyId != null ? String(defaultCompanyId) : '';

	/* Options loaded on open */
	const [typeOptions, setTypeOptions] = useState<QuickAddTypeConfig[]>([]);
	const [companies, setCompanies] = useState<CompanyOption[]>([]);
	const [optionsLoading, setOptionsLoading] = useState(false);
	const [loadError, setLoadError] = useState<string | null>(null);

	/* The people being filled in, plus their errors keyed by draft key */
	const [drafts, setDrafts] = useState<QuickAddDraft[]>([]);
	const [fieldErrors, setFieldErrors] = useState<Record<string, QuickAddDraftErrors>>({});
	const [submitErrors, setSubmitErrors] = useState<Record<string, string>>({});
	const [submitting, setSubmitting] = useState(false);

	/* Everyone saved since the modal opened — drives the summary panel */
	const [created, setCreated] = useState<CreatedSummary[]>([]);
	const [showSummary, setShowSummary] = useState(false);

	/** The type a new card starts on: CSR when allowed, else the first allowed. */
	const startingType = useCallback((options: QuickAddTypeConfig[]): QuickAddPersonType => (
		options.some((t) => t.value === DEFAULT_QUICK_ADD_TYPE) ? DEFAULT_QUICK_ADD_TYPE : (options[0]?.value ?? DEFAULT_QUICK_ADD_TYPE)
	), []);

	/** Start over with a single blank card. */
	const resetDrafts = useCallback((options: QuickAddTypeConfig[], prefillName = '') => {
		/* Whatever was typed into the search that opened us becomes the first
		   person's name, so "Dana Ortiz" → no results → Quick Add carries on. */
		setDrafts([{ ...emptyQuickAddDraft(startingType(options), defaultCompany), ...splitName(prefillName) }]);
		setFieldErrors({});
		setSubmitErrors({});
	}, [defaultCompany, startingType]);

	/* On open: clear any previous session and load the type + business options. */
	useEffect(() => {
		if (!isOpen) return;
		let cancelled = false;
		setCreated([]);
		setShowSummary(false);
		setLoadError(null);
		setOptionsLoading(true);

		Promise.all([userService.getAssignableRoles(), userService.listCompanies()])
			.then(([roles, companyOptions]) => {
				if (cancelled) return;
				const types = availableQuickAddTypes(roles);
				setTypeOptions(types);
				setCompanies(companyOptions);
				resetDrafts(types, initialName);
				if (types.length === 0) setLoadError("Your role can't add people from here. Ask an admin to add them in Admin → Users.");
			})
			.catch(() => {
				if (!cancelled) setLoadError('Could not load businesses. Please close this and try again.');
			})
			.finally(() => {
				if (!cancelled) setOptionsLoading(false);
			});

		return () => { cancelled = true; };
	// eslint-disable-next-line react-hooks/exhaustive-deps -- initialName is read once per open, not re-applied as it changes
	}, [isOpen, resetDrafts]);

	/* Escape closes THIS modal only. Captured on window — before the shared
	   Modal's document listener and the workflow overlay's — and stopped there,
	   so the layer underneath stays open. */
	useEffect(() => {
		if (!isOpen) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key !== 'Escape') return;
			e.stopImmediatePropagation();
			if (!submitting) onClose();
		};
		window.addEventListener('keydown', onKey, true);
		return () => window.removeEventListener('keydown', onKey, true);
	}, [isOpen, onClose, submitting]);

	/* Business options for the picker — deduped and alphabetized once. */
	const companyOptions = useMemo(
		() => dedupeAndSort(companies.map((c) => ({ value: String(c.id), label: c.name }))),
		[companies],
	);

	/** Replace one card's draft and clear its stale errors. */
	const updateDraft = (next: QuickAddDraft) => {
		setDrafts((prev) => prev.map((d) => (d.key === next.key ? next : d)));
		setFieldErrors((prev) => (prev[next.key] ? { ...prev, [next.key]: {} } : prev));
	};

	/** Append another blank card. */
	const addDraft = () => setDrafts((prev) => [...prev, emptyQuickAddDraft(startingType(typeOptions), defaultCompany)]);

	/** Drop a card. */
	const removeDraft = (key: string) => setDrafts((prev) => prev.filter((d) => d.key !== key));

	/**
	 * Validate every card, then create the people one at a time. Sequential on
	 * purpose: a duplicate email on person 2 should not stop person 1, and the
	 * server's reason has to land on the right card.
	 */
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (submitting) return;

		const errors: Record<string, QuickAddDraftErrors> = {};
		drafts.forEach((d) => {
			const result = validateQuickAddDraft(d);
			if (Object.keys(result).length > 0) errors[d.key] = result;
		});
		setFieldErrors(errors);
		setSubmitErrors({});
		if (Object.keys(errors).length > 0) return;

		setSubmitting(true);
		const saved: CreatedSummary[] = [];
		const failed: Record<string, string> = {};
		for (const draft of drafts) {
			try {
				const result = await userService.create(quickAddPayload(draft));
				saved.push({
					id: String(result.user.id),
					name: `${result.user.firstName} ${result.user.lastName}`.trim(),
					typeLabel: quickAddType(draft.type).label,
					email: result.user.email,
					phone: result.user.phone,
					tempPassword: result.user.email ? result.tempPassword : null,
				});
			} catch (err) {
				failed[draft.key] = apiErrorMessage(err, 'Could not add this person. Please try again.');
			}
		}
		setSubmitting(false);

		/* Hand the saved people to the hours editor right away. */
		if (saved.length > 0) {
			onCreated(saved.map(({ id, name, email, phone }) => ({ id, name, email, phone })));
			setCreated((prev) => [...prev, ...saved]);
		}

		/* Only the cards that failed stay on screen. */
		setDrafts((prev) => prev.filter((d) => failed[d.key]));
		setSubmitErrors(failed);
		if (Object.keys(failed).length === 0) setShowSummary(true);
	};

	/* ============================================
	 * RENDER PIECES
	 * ============================================ */

	/** The "who was added" summary shown once everyone is saved. */
	const renderSummary = () => (
		<div className="quick-add-users-modal__summary flex flex-col gap-12px">
			<Alert variant="success">
				{`${created.length === 1 ? '1 person was' : `${created.length} people were`} added.`}
			</Alert>
			<ul className="flex flex-col gap-8px">
				{created.map((person) => (
					<li key={person.id} className="rounded-lg border border-brand-border bg-brand-surface p-12px">
						<p className="text-14px font-sans font-semibold text-brand-text">
							{person.name} <span className="font-normal text-brand-text-muted">{`· ${person.typeLabel}`}</span>
						</p>
						{person.email ? (
							<p className="text-13px font-sans text-brand-text-muted">{`Sign-in instructions emailed to ${person.email}.`}</p>
						) : (
							<p className="text-13px font-sans text-brand-text-muted">No email — they can be assigned and given hours, but can't sign in.</p>
						)}
						{person.tempPassword && <TempPasswordDisplay value={person.tempPassword} className="mt-8px" />}
					</li>
				))}
			</ul>
		</div>
	);

	/** The create form: one card per person plus "+ Add another person". */
	const renderForm = () => (
		<form id={FORM_ID} onSubmit={handleSubmit} className="quick-add-users-modal__form flex flex-col gap-16px" noValidate>
			{/* Partial success: some people saved, the cards below did not. */}
			{created.length > 0 && (
				<Alert variant="success">{`Added ${created.map((p) => p.name).join(', ')}. Fix the person below and save again.`}</Alert>
			)}

			{drafts.map((draft, index) => (
				<QuickAddPersonFieldset
					key={draft.key}
					draft={draft}
					onChange={updateDraft}
					errors={fieldErrors[draft.key]}
					submitError={submitErrors[draft.key]}
					typeOptions={typeOptions}
					companyOptions={companyOptions}
					position={index + 1}
					onRemove={drafts.length > 1 ? () => removeDraft(draft.key) : undefined}
					disabled={submitting}
				/>
			))}

			{!single && (
				<LinkButton onClick={addDraft} disabled={submitting} className="self-start">
					+ Add another person
				</LinkButton>
			)}
		</form>
	);

	/** Footer actions for whichever panel is showing. */
	const renderFooter = () => {
		if (optionsLoading) return undefined;
		if (showSummary) {
			return (
				<div className="flex flex-col-reverse tablet:flex-row tablet:items-center tablet:justify-end gap-12px">
					{!single && <Button variant="ghost" onClick={() => { setShowSummary(false); resetDrafts(typeOptions); }} className="w-full tablet:w-auto">Add more people</Button>}
					<Button variant="primary" onClick={onClose} className="w-full tablet:w-auto">Done</Button>
				</div>
			);
		}
		return (
			<div className="flex flex-col-reverse tablet:flex-row tablet:items-center tablet:justify-end gap-12px">
				<Button variant="ghost" onClick={onClose} disabled={submitting} className="w-full tablet:w-auto">Cancel</Button>
				<Button type="submit" form={FORM_ID} variant="primary" isLoading={submitting} disabled={typeOptions.length === 0 || drafts.length === 0} className="w-full tablet:w-auto">
					{drafts.length > 1 ? `Add ${drafts.length} people` : 'Add person'}
				</Button>
			</div>
		);
	};

	if (!isOpen) return null;

	/* Stop React events at the portal boundary — they would otherwise bubble
	   through the component tree to the form / overlay that opened us. */
	const stop = (e: React.SyntheticEvent) => e.stopPropagation();

	return createPortal(
		<div className="quick-add-users-modal" onClick={stop} onSubmit={stop} onKeyDown={stop} onFocus={stop} onMouseDown={stop} onChange={stop}>
			{/* !z-[80]: it can open from inside a ConfirmModal (z-[70]) — the
			    sign-off hand-off prompt — so it must stack above every layer. */}
			<Modal isOpen={isOpen} onClose={submitting ? () => undefined : onClose} title={showSummary ? 'People added' : 'Quick Add User'} size="md" footer={renderFooter()} className="!z-[80]" dataRef="organism-quick-add-users-modal">
				{optionsLoading ? (
					<div className="flex items-center justify-center py-32px"><Spinner size="md" /></div>
				) : loadError ? (
					<Alert variant="error">{loadError}</Alert>
				) : showSummary ? (
					renderSummary()
				) : (
					renderForm()
				)}
			</Modal>
		</div>,
		document.body,
	);
};

export default QuickAddUsersModal;
